import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useVerifyClaim } from "./useVerifyClaim";
import { useLoadingSteps, type LoadingStep } from "../../hooks/useLoadingSteps";
import type { ApiError } from "../../lib/errors";
import type { ClaimWithSource, VerifyResponse } from "../../types/claims";
import { ROUTES } from "../../route_paths";

const STEP_DELAYS = [0, 1000, 2000, 3000, 4000];

interface UseAnalysingResult {
  activeStepIndex: number;
  subMessage: string;
  steps: LoadingStep[];
  rateLimitError: { retryAfter: number } | null;
  apiError: ApiError | null;
  claim: ClaimWithSource | undefined;
}

export function useAnalysing(): UseAnalysingResult {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const claim = (location.state as { claim?: ClaimWithSource } | null)?.claim;

  const steps: LoadingStep[] = useMemo(
    () =>
      (
        t("analysing.steps", { returnObjects: true }) as { label: string; subMessages: string[] }[]
      ).map((s, i) => ({ ...s, delay: STEP_DELAYS[i] })),
    [t]
  );

  const { execute } = useVerifyClaim();
  const { activeStepIndex, subMessage } = useLoadingSteps(steps);

  const [apiResult, setApiResult] = useState<VerifyResponse | null>(null);
  const [rateLimitError, setRateLimitError] = useState<{ retryAfter: number } | null>(null);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const hasFetched = useRef(false);
  const hasNavigated = useRef(false);

  useEffect(() => {
    if (!claim) navigate(ROUTES.landing.path());
  }, [claim, navigate]);

  useEffect(() => {
    if (!claim || hasFetched.current) return;
    hasFetched.current = true;
    execute(claim).then((outcome) => {
      if (outcome.status === "rate_limited") {
        setRateLimitError({ retryAfter: outcome.retryAfter });
        return;
      }
      if (outcome.status === "error") {
        setApiError(outcome.error);
        return;
      }
      const result = outcome.data;
      if (result.cache_hit) {
        navigate(
          ROUTES.verify.path({ userEventVerificationId: result.user_event_verification_id! }),
          {
            state: { result: { ...result, raw_text: claim.source_text } },
          }
        );
        return;
      }
      setApiResult(result);
    });
  }, [claim, execute, navigate]);

  useEffect(() => {
    if (apiResult && claim && !hasNavigated.current) {
      hasNavigated.current = true;
      navigate(
        ROUTES.verify.path({ userEventVerificationId: apiResult.user_event_verification_id! }),
        {
          state: { result: { ...apiResult, raw_text: claim.source_text } },
        }
      );
    }
  }, [apiResult, activeStepIndex, claim, navigate]);

  return { activeStepIndex, subMessage, steps, rateLimitError, apiError, claim };
}
